<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { derived as sderived, writable } from 'svelte/store'
    import type { Readable, Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { cloneDeep, isEqual } from 'lodash-es'
    import type { DateTime } from 'luxon'
    import { filter } from 'rxjs/operators'

    import type { CrumbsReadableStore } from 'hydria/components/breadcrumb'
    import { iconExclamationTriangle } from 'hydria/icons'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, groups as userGroups, type SimpleFetchResult } from 'hydria/services/session'
    import { spath } from 'hydria/svelte_helpers'
    import { toLuxon } from 'hydria/utils/time'

    import type { IDomain } from 'wdom/domain/interfaces'
    import { contactTypeOrder, makeCompareContact, sortContacts } from 'wdom/services/contacts'
    import type { IAddressGroup, IContact } from 'wdom/services/contacts'
    import { stream } from 'wdom/services/stream'
    import type { StreamEvent } from 'wdom/services/stream'
    import { Breadcrumb } from 'breadcrumb'

    import type { DomainHandler } from './domain'

    import Dropdown from 'hydria/components/Dropdown.svelte'
    import ContactGroupEditor from 'wdom/components/contacts/ContactGroupEditor.svelte'
    import DropdownEntry from './ContactHistory/DropdownEntry.svelte'
    import HistoryDropdownPlaceholder from './ContactHistory/DropdownPlaceholder.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const domainIdna = getContext('domainIdna') as Readable<string>
    const Domain = getContext('Domain') as DomainHandler

    const status = sderived(Domain.status, ($status) => $status)

    let domain: IDomain = $derived($status == 'ready' ? Domain.domain : ({} as IDomain))

    let inlineModal: boolean = $state(true)

    let preferedLg: string | undefined = $state()

    const contactTaskRunning = sderived(Domain.runningTasks.contact, ($running) => $running)
    // Set to true when we just sent a save, and are expecting the task creation to pop soon through the stream.
    let expectingTask = $state(false)
    // Indicates the contacts have changed, and should be reloaded.
    let contactsChanged = $state(false)
    unsubs.push(
        stream.pipe(filter((e) => (e as StreamEvent).event == 'task' && (e as StreamEvent).data.request_type == 'set_domain_contacts' && (e as StreamEvent).data.tags.includes(`idna:${$domainIdna}`))).subscribe({
            next: (e) => {
                // We don't actually handle the task here, it's already done "properly" in the Domain object.
                if (expectingTask) {
                    expectingTask = false
                }
                if ((e as StreamEvent).data.state == 'SUCCESS') {
                    contactsChanged = true
                }
            },
        }),
    )

    // We could listen to the stream, but since Domain already loads the contact data for us,
    // we don't actually need to reload everything and handle everything ourselves.
    // 3 state: initial load null -> false
    // if we get a "contactsLoading" when false, then new contacts have been received and can be loaded into the UI
    let newBackgroundContacts: boolean | null = $state(null)
    const contactsLoading = sderived(Domain.loadingContacts, ($v) => $v)

    unsubs.push(
        contactsLoading.subscribe((v) => {
            if (newBackgroundContacts === null && $contactsLoading) {
                newBackgroundContacts = false
            } else if (newBackgroundContacts === false && !$contactsLoading) {
                newBackgroundContacts = true
            }
        }),
    )

    let contactStatus: 'loading' | 'saving' | 'ready' = $state('loading')
    const tldValidity: Writable<{ [key: string]: boolean }> = writable({})

    let editionAllowed = true

    let addresses: IAddressGroup[] = $state([])
    async function loadAddresses() {
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', `/json/wdom-user/1/${domain.owner}/addresses`)
        } catch {
            // Just be silent for now
            addresses = []
            return
        }
        loadAddressesData(r.data)
    }

    let addressesLoaded = false
    $effect(() => {
        if (!addressesLoaded && domain && domain.owner) {
            addressesLoaded = true
            loadAddresses()
        }
    })

    function loadAddressesData(data: any) {
        let laddresses: any[] = []
        // mainAddressIsValid = true
        for (const [i, ladd] of data.addresses.entries()) {
            let cgroup = {
                source: ladd,
                isMain: i === data.base_index,
                isDefaultBilling: i === data.billing_index,
                title: ladd.title,
                id: ladd.id,
            }
            if (cgroup.isMain) {
                cgroup.title = tgettext('Account address')
                cgroup.id = 'main'
            }

            // Normally an address is always "ok".
            // However, on a new account, we may have no address, or only a name/org.
            // if (cgroup.isMain && !((ladd.org || ladd.name) && ladd.city && ladd.street && ladd.street.length)) {
            //     mainAddressIsValid = false
            // }
            laddresses.push(cgroup)
        }
        addresses = laddresses
    }

    // ##############################################################  Load Contact with log
    interface IContactLog {
        contacts: IContact[]
        log_id: string
        stamp: DateTime
    }
    let contactLog: IContactLog[] = $state([])

    // These are the contacts we send to the editor as the "currently saved".
    let contacts: IContact[] = $state([])
    let originalContacts: IContact[] | null = $state(null)

    // This acts as a signal to the group editor: set the edited value to this
    let setContacts: IContact[] = $state([])

    // This is what the editor updates when there are changes
    const contactsEdited: Writable<IContact[]> = writable([])

    let loading = false
    async function loadContact() {
        let r: SimpleFetchResult
        if (loading) {
            return
        }
        loading = true
        try {
            r = await simpleFetch('GET', `/json/domain/1/${$domainIdna}`, { with_contacts: '1', with_contacts_log: '5', with_short_domain_list: '1' })
        } catch {
            return
        }
        const log: IContactLog[] = []
        for (const item of r.data.contacts_log) {
            for (const c of item.document.contacts) {
                if (c.type == 'owner') c.type = 'registrant'
            }
            log.push({
                stamp: toLuxon(item.stamp),
                log_id: item.log_id,
                contacts: sortContacts(item.document.contacts),
            })
        }
        contactLog = log
        for (const c of r.data.contacts) {
            if (c.type == 'owner') c.type = 'registrant'
        }
        originalContacts = sortContacts(r.data.contacts)
        contacts = cloneDeep(originalContacts)
        currentRevision = r.data.contacts_log_id

        shortDomainList = r.data.short_domain_list.filter((entry: IShortDomain) => entry.idna != $domainIdna)

        preferedLg = r.data.prefered_lg

        loading = false
    }

    function resetToSaved() {
        if (contactLog.length) {
            loadContactFromRevision(contactLog[0])
        }
    }

    // ##############################################################  Load Short Domain List
    interface IShortDomain {
        idna: string
        name: string
    }
    let shortDomainList: IShortDomain[] = $state([])
    // async function loadShortDomainList() {
    //     const r = await simpleFetch('GET', '/json/domain/1/contact-domain-list')
    //     shortDomainList = r.data.domains.filter((entry: IShortDomain) => entry.id != $domainIdna)
    // }

    let shortDomainSelectionTypes = $state({
        registrant: true,
        admin: true,
        billing: true,
        tech: true,
    })
    const contactTexts = {
        registrant: 'Titulaire',
        admin: 'Administratif',
        billing: 'Facturation',
        tech: 'Technique(s)',
    }

    let showShortDomainSelection = $state(false)
    async function importShortDomain(sdomain: IShortDomain) {
        showShortDomainSelection = false

        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', `/json/domain/1/${sdomain.idna}`, { with_contacts: '1' })
        } catch (e) {
            notify({
                type: 'danger',
                title: $_('Contacts'),
                text: $_('Failed to load contacts from “{domain}”', { domain: sdomain.name }),
            })
            return
        }

        let lcontacts: IContact[] = []

        for (const c of r.data.contacts) {
            if (c.type == 'owner') c.type = 'registrant'
        }
        for (const [ctype, isSel] of Object.entries(shortDomainSelectionTypes)) {
            const eqType = ['registrant', 'owner'].includes(ctype) ? ['registrant', 'owner'] : [ctype]
            if (isSel && (ctype != 'registrant' || canEditRegistrant)) {
                // Import that contact...
                lcontacts = [...lcontacts, ...r.data.contacts.filter((lc) => eqType.includes(lc.type))]
            } else {
                // Keep the original contact
                lcontacts = [...lcontacts, ...cloneDeep(contacts.filter((lc) => eqType.includes(lc.type)))]
            }
        }
        setContacts = sortContacts(lcontacts)
        currentRevision = undefined
    }

    // ##############################################################  Breadcrumbs
    const crumbs: CrumbsReadableStore = sderived(status, ($status) => [
        { txt: $_('Domaines'), url: spath('/domains') },
        {
            txt: $status == 'ready' ? Domain.unicode : $status == 'loading' ? '…' : iconExclamationTriangle,
            html: $status != 'ready' && $status != 'loading',
            url: $status == 'ready' ? spath(`/domain/${$domainIdna}`) : undefined,
        },
        { txt: $_('Contacts') },
    ])

    // ##############################################################  Revisions
    let contactLogOpen = $state(false)
    let currentRevision: string | undefined = $state(undefined)

    function loadContactFromRevision(entry: IContactLog) {
        currentRevision = entry.log_id
        contactLogOpen = false
        setContacts = entry.contacts.map((c) => cloneDeep(c))
    }

    // ##############################################################  Save
    async function save() {
        contactStatus = 'saving'
        try {
            await simpleFetch('POST', `/json/domain/1/${$domainIdna}/contacts`, { contacts: $contactsEdited })
            notify({
                type: 'info',
                title: $_('Contacts'),
                text: $_('The contacts are being saved on the domain.'),
            })
            expectingTask = true
        } catch (e) {
            notify({
                type: 'danger',
                title: $_('Contacts'),
                text: $_('The contact information could not be saved.'),
            })
        }
        contactStatus = 'ready'
    }

    // Signal to tell CGE to reset the contacts
    let resetContacts: boolean = $state(false)
    async function loadNewContacts() {
        await loadContact()
        contactStatus = 'ready'
        resetToSaved()

        resetContacts = true

        // contactStatus = 'loading'
        contactsChanged = false
        newBackgroundContacts = null
    }

    onDestroy(() => {
        if (showShortDomainSelection && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })

    let canEditRegistrant = $derived($userGroups.has('cira-can-change-registrant'))

    unsubs.push(
        status.subscribe(($status) => {
            if (contactStatus == 'loading' && $status == 'ready' && $contactTypeOrder && !loading) {
                loadContact().then(() => {
                    contactStatus = 'ready'
                })
            }
        }),
    )
    unsubs.push(
        contactTypeOrder.subscribe(($contactTypeOrder) => {
            if (contactStatus == 'loading' && $status == 'ready' && $contactTypeOrder && !loading) {
                loadContact().then(() => {
                    contactStatus = 'ready'
                })
            }
        }),
    )

    let originalContactsForComparison: IContact[] | null = $derived(originalContacts == null ? null : (originalContacts as any).filter((c) => c.type != 'registrant' || canEditRegistrant).map((c) => makeCompareContact(c, preferedLg)))

    let contactsEditedForComparison = $derived($contactsEdited == null ? null : $contactsEdited.filter((c) => c.type != 'registrant' || canEditRegistrant).map((c) => makeCompareContact(c, preferedLg)))
    let dirty = $derived(originalContactsForComparison != null && contactsEditedForComparison != null && !isEqual(contactsEditedForComparison, originalContactsForComparison))
    let allValid = $derived(domain && $tldValidity != null && $tldValidity[domain.tld == 'ca' ? 'cira' : $tldValidity[domain.tld] != null ? domain.tld : 'general'])
    $effect(() => {
        if (showShortDomainSelection && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!showShortDomainSelection && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<svelte:body
    onclick={(e) => {
        if (contactLogOpen) contactLogOpen = false
    }}
/>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            if (showShortDomainSelection && document.documentElement.classList.contains('modal-open')) {
                showShortDomainSelection = false
            }
        }
    }}
/>

<div id="adm-dom-contacts" style="display: flex; flex-direction: column;">
    <div style="flex: 0 0 auto;">
        <Breadcrumb {crumbs} />
    </div>
    <div style="flex: 1 1 0; position: relative;">
        {#if $status == 'not-found' || $status == 'error'}
            <div class="alert alert-danger">
                <Icon inline icon="fa:exclamation-triangle" />
                {#if $status == 'not-found'}
                    {$_('The requested document does not exist.')}
                {:else}
                    {$_('An error occured. Please try again in a moment.')}
                {/if}
            </div>
        {:else if contactStatus == 'loading'}
            <div class="card">
                <div class="card-body">
                    <div class="m-4 d-flex justify-content-center align-items-center">
                        <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
                    </div>
                </div>
            </div>
        {:else}
            <div id="container-domain" class:has-contact-panel={inlineModal && editionAllowed}>
                <div class="card card-contacts">
                    <div class="card-body">
                        <div class="card-title contact-title">
                            <div class="contacts-title">{$_('Contacts')}</div>
                            <div class="actions domain-contact-actions">
                                {#if editionAllowed}
                                    <div class="btn-group btn-group-sm">
                                        <button type="button" class="btn btn-outline-secondary" class:active={!inlineModal} onclick={() => (inlineModal = !inlineModal)}><Icon inline icon="fa:external-link" /></button>
                                    </div>
                                    <div class="btn-group btn-group-sm">
                                        <Dropdown
                                            items={contactLog}
                                            placeholderComponent={HistoryDropdownPlaceholder}
                                            onSelect={(e) => {
                                                for (const entry of contactLog) {
                                                    if (entry.log_id == e) {
                                                        loadContactFromRevision(entry)
                                                        return
                                                    }
                                                }
                                            }}
                                            size="sm"
                                            text={DropdownEntry}
                                            textIsHtml={'component'}
                                            id={'log_id'}
                                            selected={currentRevision}
                                            forcePlaceholder={true}
                                            empty={''}
                                            clear={null}
                                            disabled={contactStatus == 'saving' || $contactTaskRunning || !contactLog.length || expectingTask}
                                            alignRight={true}
                                        />
                                        <button class="btn btn-outline-secondary" type="button" onclick={() => (showShortDomainSelection = true)} disabled={contactStatus == 'saving' || $contactTaskRunning || !shortDomainList.length || expectingTask}>
                                            <Icon inline icon="fa:globe" /><span class="hidden-xs hidden-sm hidden-cpan-cond-md">&nbsp;&nbsp;{$_('Import from another domain')}</span>
                                        </button>
                                    </div>
                                    <div class="btn-group btn-group-sm">
                                        <button class="btn btn-outline-secondary" type="button" onclick={() => resetToSaved()} disabled={contactStatus == 'saving' || $contactTaskRunning || !contactLog.length || !dirty || expectingTask}>
                                            <Icon inline icon="fa:undo" /><span class="hidden-xs hidden-cpan-cond-md">&nbsp;&nbsp;{$_('Undo changes')}</span>
                                        </button>
                                        <button class="btn btn-primary" type="button" onclick={() => save()} disabled={contactStatus == 'saving' || $contactTaskRunning || !dirty || expectingTask || !allValid}>
                                            <Icon inline icon="fa:floppy-o" /><span class="hidden-xxs hidden-cpan-cond-md">&nbsp;&nbsp;{$_('Save')}</span>
                                        </button>
                                    </div>
                                {/if}<!-- edition allowed -->
                            </div>
                        </div>

                        {#if domain.system == 'webnames' && !Domain.webnamesCapabilities['contacts']}
                            <p>{@html $_('To manage the contacts for this domain, please contact us by phone at <a href="tel:8778809224">877 880-9224</a> or by email at <a href="mailto:tech@webdomaine.ca">tech@webdomaine.ca</a>.')}</p>
                        {/if}

                        {#if $contactTaskRunning}
                            <p class="text-warning">
                                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>&nbsp;&nbsp;{$_('The contacts are currently being changed. Please wait a moment.')}
                            </p>
                        {:else if contactsChanged}
                            <p class="text-warning">
                                {$_('The contacts have changed on the server.')}
                                <button type="button" class="btn btn-primary" onclick={() => loadNewContacts()}>{$_('Load new contact information.')}</button>
                            </p>
                        {/if}

                        {#if domain.system != 'webnames' || Domain.webnamesCapabilities['contacts']}
                            <ContactGroupEditor context="domain" {contacts} bind:setContacts {contactsEdited} updating={contactStatus != 'ready' || $contactTaskRunning || newBackgroundContacts || contactsChanged} domains={[Domain.unicode]} {canEditRegistrant} {addresses} inlineMode={inlineModal} {tldValidity} bind:resetContacts lg={preferedLg} />
                        {/if}
                    </div>
                </div>
            </div>
        {/if}

        {#if showShortDomainSelection}
            <div class="modal modal-contact-alt-domain" role="dialog" transition:fade={{ duration: 250 }}>
                <div class="modal-dialog modal-lg">
                    <div class="modal-content">
                        <div class="modal-header text-bg-primary">
                            <div>{$_('Load contacts from another domain')}</div>
                        </div>
                        <div class="modal-body">
                            <div class="alt-domain-contact-selection">
                                {#each shortDomainList as sdomain}
                                    <div>
                                        <a
                                            href="/"
                                            onclick={(e) => {
                                                e.preventDefault()
                                                importShortDomain(sdomain)
                                            }}>{sdomain.name}</a
                                        >
                                    </div>
                                {/each}
                            </div>
                        </div>
                        <div class="modal-footer">
                            {#each Object.entries(shortDomainSelectionTypes) as [ttype, tval]}
                                <label for="short-c-{ttype}" style="white-space: nowrap; margin-right: 0.5em">
                                    <input type="checkbox" id="short-c-{ttype}" bind:checked={shortDomainSelectionTypes[ttype]} />
                                    {contactTexts[ttype]}
                                </label>
                            {/each}
                            <button type="button" class="btn btn-outline-secondary" onclick={() => (showShortDomainSelection = false)}>{$_('Cancel')}</button>
                        </div>
                    </div>
                </div>
                <div class="modal-backdrop" onclick={() => (showShortDomainSelection = false)} aria-hidden="true"></div>
            </div>
        {/if}
    </div>
</div>
